import { Link } from 'react-router-dom';
import { ArrowRight, ShieldCheck } from 'lucide-react';
import { AcceptedLogos, usePaymentMethods } from './PaymentMethods';
import { paymentMethodsSentence } from '../lib/paymentMethods';

/**
 * "Compra sin cuenta": el bloque que responde, justo debajo del hero, la duda
 * que más frena una primera compra — ¿me van a hacer registrarme y llenar
 * formularios? No. Son tres pasos y el copy dice exactamente lo que pasa en
 * el flujo real (carrito → Stripe → WhatsApp), nada que el checkout no cumpla.
 *
 * Va sobre --paper-2: es la hoja que el rasgado de la portada monta sobre la
 * franja de colores del hero, así que el primer bloque después del hero es
 * literalmente "la siguiente hoja".
 */
const STEPS = [
    {
        title: 'Elige tu papel',
        text: 'Agrega al carrito lo que necesitas, por paquete o por kilo. Sin registrarte.',
    },
    {
        title: 'Paga como prefieras',
        // El texto se arma con los métodos activos (ver el componente).
        cards: true,
    },
    {
        title: 'Elige cómo enviarlo',
        options: [
            ['Cotízalo tú mismo', 'en el carrito, con tu código postal, y pagas todo junto.'],
            ['O por WhatsApp', ': pagas el papel y te cotizamos el envío con tu pedido ya escrito.'],
        ],
    },
];

export default function BuyWithoutAccount() {
    const methods = usePaymentMethods();
    const sentence = paymentMethodsSentence(methods);
    const pagoTexto = `Con ${sentence}. Solo te pedimos tu correo para el recibo.`;
    return (
        <section style={{ backgroundColor: 'var(--paper-2)' }} className="border-b border-[var(--line)]" data-testid="buy-without-account">
            <div className="max-w-7xl mx-auto px-6 md:px-12 pt-8 pb-12 md:pt-10 md:pb-16">
                <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-12 items-end mb-10">
                    <div className="lg:col-span-7">
                        <p className="eyebrow mb-4">Compra sin cuenta</p>
                        <h2 className="font-heading text-[clamp(2rem,4.4vw,3.5rem)] font-black leading-[0.95] tracking-tight text-balance" style={{ color: 'var(--ink)' }}>
                            Sin registros ni formularios.{' '}
                            <span style={{ color: 'var(--kraft-deep)' }}>Compra en tres pasos.</span>
                        </h2>
                    </div>
                    <div className="lg:col-span-5 flex flex-col items-start lg:items-end gap-3">
                        <Link
                            to="/catalogo"
                            data-testid="buy-now-cta"
                            className="group inline-flex items-center gap-2.5 h-[52px] px-8 rounded-sm uppercase font-bold tracking-widest text-[13px] hover:opacity-85"
                            style={{ backgroundColor: 'var(--ink)', color: 'var(--paper)' }}
                        >
                            Empezar a comprar
                            <ArrowRight className="w-4 h-4 transition-transform duration-300 group-hover:translate-x-1" />
                        </Link>
                        <p className="flex items-center gap-2 text-xs" style={{ color: 'var(--ink-soft)' }}>
                            <ShieldCheck className="w-4 h-4 flex-shrink-0" strokeWidth={1.5} style={{ color: 'var(--success)' }} />
                            Pago seguro · Sin crear cuenta
                        </p>
                    </div>
                </div>

                <ol className="spec-strip spec-strip--3" aria-label="Cómo comprar en Carpepel">
                    {STEPS.map((s, i) => (
                        <li key={s.title} className="spec-item reveal-on-scroll grid grid-cols-[2.25rem_1fr] gap-x-3 sm:block" data-testid={`buy-step-${i + 1}`}>
                            <span className="row-span-2 block font-heading font-black leading-none sm:mb-4" style={{ color: 'var(--kraft)', fontSize: 'clamp(2.5rem, 4vw, 3.25rem)' }} aria-hidden="true">
                                {i + 1}
                            </span>
                            <h3 className="font-heading text-lg font-bold uppercase tracking-wide mb-1 sm:mb-2" style={{ color: 'var(--ink)' }}>
                                {s.title}
                            </h3>
                            {(s.text || s.cards) && (
                                <p className="text-sm leading-relaxed max-w-xs" style={{ color: 'var(--ink-soft)' }}>
                                    {s.cards ? pagoTexto : s.text}
                                </p>
                            )}
                            {s.cards && <AcceptedLogos className="h-6 mt-3" />}
                            {s.options && (
                                <ul className="text-sm leading-relaxed max-w-xs space-y-1.5" style={{ color: 'var(--ink-soft)' }}>
                                    {s.options.map(([lead, rest]) => (
                                        <li key={lead}><strong className="font-bold" style={{ color: 'var(--ink)' }}>{lead}</strong>{rest.startsWith(':') ? rest : ` ${rest}`}</li>
                                    ))}
                                </ul>
                            )}
                        </li>
                    ))}
                </ol>
            </div>
        </section>
    );
}
